import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useConfirmVisit } from '@/features/visits/hooks/mutation/use-confirm-visit';
import { useRejectVisit } from '@/features/visits/hooks/mutation/use-reject-visit';
import { useDialog } from '@/hooks/use-dialog';

type VisitorsDialogAction = 'confirm' | 'reject';

interface VisitorsDialogPayload {
  visitId: number;
  visitorFullName: string;
}

interface VisitorsDialogState {
  action: VisitorsDialogAction;
  payload: VisitorsDialogPayload;
}

export interface VisitorsActionDialogConfig {
  title: string;
  description: string;
  confirmButtonText: string;
  variant: 'info' | 'warning';
  isSubmitting: boolean;
  onConfirm: () => void;
}

export const useVisitorsActionDialog = () => {
  const { t } = useTranslation(['visits']);
  const [isOpen, openDialog, closeDialog] = useDialog();
  const [state, setState] = useState<VisitorsDialogState | null>(null);

  const handleCloseDialog = () => {
    closeDialog();
    setState(null);
  };

  const confirmVisit = useConfirmVisit();
  const rejectVisit = useRejectVisit();

  const openConfirmDialog = (payload: VisitorsDialogPayload) => {
    setState({ action: 'confirm', payload });
    openDialog();
  };

  const openRejectDialog = (payload: VisitorsDialogPayload) => {
    setState({ action: 'reject', payload });
    openDialog();
  };

  const getDialogConfig = () => {
    if (!state) {
      return null;
    }

    switch (state.action) {
      case 'confirm':
        return {
          title: t('visits:table.actionsDialog.confirm.title'),
          description: t('visits:table.actionsDialog.confirm.description', {
            fullName: state.payload.visitorFullName,
          }),
          confirmButtonText: t('visits:table.actionsDialog.confirm.confirmButton'),
          variant: 'info' as const,
          isSubmitting: confirmVisit.isPending,
          onConfirm: () =>
            confirmVisit.mutate(state.payload.visitId, {
              onSuccess: handleCloseDialog,
            }),
        };
      case 'reject':
        return {
          title: t('visits:table.actionsDialog.reject.title'),
          description: t('visits:table.actionsDialog.reject.description', {
            fullName: state.payload.visitorFullName,
          }),
          confirmButtonText: t('visits:table.actionsDialog.reject.confirmButton'),
          variant: 'warning' as const,
          isSubmitting: rejectVisit.isPending,
          onConfirm: () =>
            rejectVisit.mutate(state.payload.visitId, {
              onSuccess: handleCloseDialog,
            }),
        };
      default:
        return null;
    }
  };

  return {
    isOpen,
    openConfirmDialog,
    openRejectDialog,
    closeDialog: handleCloseDialog,
    dialogConfig: getDialogConfig(),
    isActionInProgress: confirmVisit.isPending || rejectVisit.isPending,
  };
};
